import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  Output,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TemplateDirective } from '@proget-shared';
import { ObjectHelper, StringHelper } from '@proget-shared/helper';
import { ScrollbarComponent } from '@proget-shared/ui/scrollbar';
import { BehaviorSubject, combineLatest, filter, noop, Subject, Subscription } from 'rxjs';

import { ListboxElement } from './listbox-element.type';
import { ListboxSort } from './listbox-sort.type';

type HoverSource = 'mouse' | 'keyboard' | 'focus';

@Component({
  selector: 'app-listbox',
  templateUrl: './listbox.component.html',
  styleUrls: ['./listbox.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ListboxComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class ListboxComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {
  public readonly form: FormGroup = new FormGroup({
    selection: new FormControl<any[]>([]),
    allSelected: new FormControl<boolean>(false),
    filter: new FormControl<string>(''),
  });

  @Input()
  public tabindex = '0';
  @Input()
  public filterPlaceholder = '';
  public hoverIndex = -1;

  @ViewChild('defaultItemTemplate', { read: TemplateRef })
  protected defaultItemTemplate: TemplateRef<any>;
  protected itemTemplate: TemplateRef<any>;

  private readonly optionsSubject = new BehaviorSubject<any[]>([]);
  private readonly valueSubject = new Subject<{ values: any[]; known: boolean }>();
  private readonly refreshSubject = new Subject<void>();
  private readonly subscription = new Subscription();
  private readonly config = {
    checkbox: false,
    filter: false,
    multiple: false,
    showToggleAll: true,
    valueKey: '',
    sort: false as ListboxSort,
    disabledOptions: null as ((item: any) => boolean) | null,
  };

  @Output()
  private onDblClick: EventEmitter<any> = new EventEmitter<any>();
  @Output()
  private onClick: EventEmitter<any> = new EventEmitter<any>();
  @Output()
  private onFilter: EventEmitter<string> = new EventEmitter<string>();
  @ViewChild('scrollContainer')
  private scrollContainer: ScrollbarComponent;
  @ViewChild('scrollContainer', { read: ElementRef })
  private scrollbarEl: ElementRef<HTMLDivElement>;
  @ViewChild('listItemTemplate')
  private listElementTemplate: TemplateRef<any>;
  @ViewChild('list', { read: ViewContainerRef })
  private listContainerRef: ViewContainerRef;
  @ContentChildren(TemplateDirective)
  private templates: QueryList<TemplateDirective>;
  private modelTouched: () => void = noop;
  private modelChanged: (values: any) => void = noop;
  private listboxElements: ListboxElement[] = [];
  private visibleElements: ListboxElement[] = [];
  private hoverSource: HoverSource = 'mouse';

  constructor(
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef
  ) {
    this.subscription.add(
      this.form.get('selection').valueChanges
        .subscribe({
          next: (values) => {
            this.valueSubject.next({ values, known: true });

            this.modelTouched();
            this.modelChanged(this.prepareOutputValue());
          },
        })
    );

    this.subscription.add(
      combineLatest([this.optionsSubject, this.valueSubject])
        .subscribe(([options, { values, known }]) => {
          const optionsValues = options.map((option) => this.getItemValue(option));
          const knownValues = known
            ? values
            : values
              .map((value) => optionsValues.find((optionsValue) => ObjectHelper.equals(value, optionsValue)))
              .filter((item) => void 0 !== item);

          this.form.get('selection').setValue(knownValues, { emitEvent: false });
          this.updateToggleAllCheckbox();
        })
    );

    this.subscription.add(
      combineLatest([this.optionsSubject, this.refreshSubject])
        .subscribe(([options]) => {
          this.buildList(options);
        })
    );

    this.subscription.add(
      this.form.get('filter').valueChanges
        .subscribe({
          next: (filterValue) => {
            this.onFilter.emit(filterValue);
            this.updateVisibleItems();
          },
        })
    );

    this.subscription.add(
      this.form.get('allSelected').valueChanges
        .pipe(filter((allSelected) => allSelected))
        .subscribe({
          next: () => {
            const unselectedVisibleValues = this.visibleElements
              .filter((element) => !this.isItemSelected(element.item) && !element.disabled)
              .map((element) => this.getItemValue(element.item));

            this.selection = this.selection.concat(unselectedVisibleValues);
          },
        })
    );

    this.subscription.add(
      this.form.get('allSelected').valueChanges
        .pipe(filter((allSelected) => !allSelected))
        .subscribe({
          next: () => {
            const selectedVisibleValues = this.visibleElements
              .filter((element) => this.isItemSelected(element.item) && !element.disabled)
              .map((element) => this.getItemValue(element.item));

            this.selection = this.selection.filter((value) => selectedVisibleValues.indexOf(value) === -1);
          },
        })
    );
  }

  writeValue(selectedItems: any): void {
    this.valueSubject.next({
      values: selectedItems ? this.forceArray(selectedItems) : [],
      known: false,
    });
  }

  registerOnChange(fn: (date: Date | string) => void): void {
    this.modelChanged = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.modelTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled === this.form.disabled) {
      return;
    }

    isDisabled ? this.form.disable({ emitEvent: false }) : this.form.enable({ emitEvent: false });
    this.cdr.detectChanges();
  }

  ngAfterViewInit(): void {
    this.setItemTemplate(this.templates.find((template) => template.appTemplate === 'item')?.templateRef);
    this.refreshSubject.next();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.optionsSubject.complete();
    this.valueSubject.complete();
    this.refreshSubject.complete();
  }

  public get allVisibleSelected(): boolean {
    const selectableElements = this.visibleElements.filter((element) => !element.disabled);

    return selectableElements.length > 0 && selectableElements.every((element) => this.isItemSelected(element.item));
  }

  public get anyVisibleSelected(): boolean {
    return this.visibleElements.some((element) => !element.disabled && this.isItemSelected(element.item));
  }

  @Input()
  public set disabled(disabled: boolean) {
    this.setDisabledState(disabled);
  }

  @Input()
  public set checkbox(visibility: boolean) {
    this.config.checkbox = visibility;
  }

  public get checkbox(): boolean {
    return this.config.multiple && this.config.checkbox;
  }

  @Input()
  public set filter(visibility: boolean) {
    this.config.filter = visibility;
  }

  public get filter(): boolean {
    return this.config.filter;
  }

  @Input()
  public set multiple(enabled: boolean) {
    this.config.multiple = enabled;

    if (!enabled) {
      this.selection = this.selection.slice(-1);
    }
  }

  public get multiple(): boolean {
    return this.config.multiple;
  }

  @Input()
  public set options(items: any) {
    this.optionsSubject.next(Array.isArray(items) ? items.slice() : []);
  }

  @Input()
  public set showToggleAll(visibility: boolean) {
    this.config.showToggleAll = visibility;
  }

  public get showToggleAll(): boolean {
    return this.checkbox && this.config.showToggleAll;
  }

  @Input()
  public set sort(sort: ListboxSort) {
    this.config.sort = sort;
    this.refreshSubject.next();
  }

  public get sort(): ListboxSort {
    return this.config.sort;
  }

  @Input()
  public set disabledOptions(fn: ((item: any) => boolean) | null) {
    this.config.disabledOptions = fn;
    this.updateDisabledItems();
  }

  public get disabledOptions(): ((item: any) => boolean) | null {
    return this.config.disabledOptions;
  }

  @Input()
  public set valueKey(key: any) {
    const selectedItems: any[] = this.getSelectedItems();

    this.config.valueKey = key;
    this.form.get('selection').setValue(
      selectedItems.map((item) => this.getItemValue(item)),
      { emitEvent: false }
    );
  }

  @HostBinding('tabindex')
  public get tabindexAttr(): string {
    return this.form.enabled ? this.tabindex : '-1';
  }

  public setItemTemplate(template: TemplateRef<any>): void {
    this.itemTemplate = template ?? this.defaultItemTemplate;
  }

  public clearFilter(): void {
    this.form.get('filter').setValue('');
  }

  public sortList(): void {
    if (!this.sort) {
      return;
    }

    if (typeof this.sort === 'function') {
      this.listboxElements.sort((a, b) => (this.sort as (a, b) => number)(a.item, b.item));
    } else {
      this.listboxElements.sort((a, b) => a.target.textContent.localeCompare(
        b.target.textContent,
        void 0,
        { numeric: true, sensitivity: 'base' }
      ));
    }

    this.listboxElements.forEach((element, index) => {
      element.index = index;
      element.target.parentElement.appendChild(element.target);
    });
  }

  public getDisplayText(searchItem: any): string {
    const searchValue = this.getItemValue(searchItem);
    const matchingElement = this.listboxElements.find((element) => ObjectHelper.equals(this.getItemValue(element.item), searchValue));

    return matchingElement?.target.textContent ?? '';
  }

  @HostBinding('class.listbox-focus')
  protected get hasFocus(): boolean {
    let currentElement = document.activeElement;

    while (currentElement !== document.body) {
      if (currentElement === this.elementRef.nativeElement) {
        return true;
      }

      currentElement = currentElement.parentElement;
    }

    return false;
  }

  protected getItemValue(item: any): any {
    if (!this.config.valueKey) {
      return item;
    }

    return item !== null && typeof item === 'object' ? item[this.config.valueKey] : null;
  }

  protected onItemClick(event: MouseEvent, element: ListboxElement): void {
    if (this.form.disabled || element.disabled) {
      return;
    }

    this.onClick.emit({
      originalEvent: event,
      option: this.getItemValue(element.item),
      value: this.prepareOutputValue(),
    });

    this.selectItem(element.item, event.ctrlKey);
  }

  protected onItemDblClick(event: MouseEvent, element: ListboxElement): void {
    this.onDblClick.emit({
      originalEvent: event,
      option: this.getItemValue(element.item),
      value: this.prepareOutputValue(),
    });
  }

  @HostListener('keydown.arrowDown', ['$event', '1'])
  @HostListener('keydown.arrowUp', ['$event', '-1'])
  protected keyNavigation(event: KeyboardEvent, direction: number): void {
    event.preventDefault();

    const searchArray = direction > 0 ? this.listboxElements : this.listboxElements.slice().reverse();
    const currentSearchIndex = searchArray.findIndex((element) => element.index === this.hoverIndex);

    if (currentSearchIndex < 0) {
      return;
    }

    const extendedArray = searchArray.slice(currentSearchIndex + 1).concat(searchArray);
    const nextVisibleElement = extendedArray.find((element) => element.visibility);

    if (!nextVisibleElement) {
      return;
    }

    this.setHoverIndex(nextVisibleElement, 'keyboard');
  }

  @HostListener('keydown.control.space', ['$event', 'true'])
  @HostListener('keydown.space', ['$event', 'false'])
  protected keySelection(event: KeyboardEvent, toggleSelection: boolean): void {
    if (this.hoverIndex === -1) {
      return;
    }

    const hoveredElement = this.listboxElements.find((element) => element.index === this.hoverIndex);

    if (!hoveredElement || !hoveredElement.visibility || hoveredElement.disabled) {
      return;
    }

    event.preventDefault();
    this.selectItem(hoveredElement.item, toggleSelection);
  }

  @HostListener('focus')
  protected onFocus(): void {
    if (this.hoverIndex === -1) {
      this.setHoverIndex(this.listboxElements.find((element) => element.visibility), 'focus');
    }
  }

  @HostListener('mousemove')
  protected onMousemove(): void {
    this.hoverSource = 'mouse';
  }

  protected noSearchResultsHidden(): boolean {
    return this.form.get('filter').value === '' || this.visibleElements.length > 0;
  }

  protected isItemSelected(item: any): boolean {
    return this.selection.indexOf(this.getItemValue(item)) !== -1;
  }

  protected setHoverIndex(element: ListboxElement, source: HoverSource = this.hoverSource): void {
    if (this.hoverSource === 'keyboard' && source === 'mouse') {
      // prevent changing index when list scrolled (mouseover was triggered)
      return;
    }

    this.hoverIndex = element ? element.index : -1;
    this.hoverSource = source;

    if (!element || source === 'mouse') {
      return;
    }

    const scrollbarElBounds = this.scrollbarEl.nativeElement.getBoundingClientRect();
    const itemBounds = element.target.getBoundingClientRect();

    if (itemBounds.top - scrollbarElBounds.top < 0) {
      this.scrollContainer.scrollToElement(element.target, { duration: 0 });
    }

    if (itemBounds.bottom - scrollbarElBounds.bottom > 0) {
      const scrollTop = this.scrollContainer.viewport.scrollTop;

      this.scrollContainer.scrollTo({ top: itemBounds.bottom - scrollbarElBounds.bottom + scrollTop, duration: 0 });
    }
  }

  private isItemDisabled(item: any): boolean {
    return this.config.disabledOptions ? this.config.disabledOptions(item) : false;
  }

  private get selection(): any[] {
    return this.form.get('selection').value;
  }

  private set selection(value: any[]) {
    this.form.get('selection').setValue(value);
  }

  private selectItem(item: any, toggleSelection: boolean): void {
    if (!this.multiple || !this.checkbox && !toggleSelection) {
      this.selection = [this.getItemValue(item)];

      return;
    }

    if (this.isItemSelected(item)) {
      this.selection = this.selection.filter((value) => value !== this.getItemValue(item));

      return;
    }

    this.selection = this.selection.concat([this.getItemValue(item)]);
  }

  private getSelectedItems(): any[] {
    return this.listboxElements
      .filter((element) => this.selection.indexOf(this.getItemValue(element.item)) !== -1)
      .map((element) => element.item);
  }

  private prepareOutputValue(): any {
    const selectedValues = this.getSelectedItems().map((item) => this.getItemValue(item));

    return this.multiple
      ? selectedValues
      : selectedValues.length
        ? selectedValues[selectedValues.length - 1]
        : null;
  }

  private buildList(items: any[]): void {
    if (!this.listContainerRef) {
      return;
    }

    const scrollTop = this.scrollContainer.viewport.scrollTop;

    // empty list
    this.listContainerRef.clear();
    this.listboxElements = [];

    // create new list
    this.listboxElements = items.map((item, index) => {
      const element = {
        item,
        visibility: true,
        disabled: this.isItemDisabled(item),
        target: null,
        index,
      };

      const viewRef = this.listContainerRef.createEmbeddedView(this.listElementTemplate, { $implicit: element });

      element.target = viewRef.rootNodes[0];

      return element;
    });

    window.requestAnimationFrame(() => {
      this.sortList();
      this.updateVisibleItems();
      this.cdr.detectChanges();
      this.scrollContainer.scrollTo({ top: scrollTop, duration: 0 });
    });
  }

  private updateVisibleItems(): void {
    const regExp = new RegExp(StringHelper.escapeRegExp(this.form.get('filter').value), 'i');

    this.listboxElements.forEach((element) => {
      element.visibility = regExp.test(element.target.textContent);
    });

    this.visibleElements = this.listboxElements.filter((element) => element.visibility);

    const selectableElements = this.visibleElements.filter((element) => !element.disabled);

    selectableElements.length && this.form.enabled
      ? this.form.get('allSelected').enable({ emitEvent: false })
      : this.form.get('allSelected').disable({ emitEvent: false });
    this.setHoverIndex(null);
    this.updateToggleAllCheckbox();
  }

  private updateToggleAllCheckbox(): void {
    const selectableElements = this.visibleElements.filter((element) => !element.disabled);

    this.form.get('allSelected').setValue(selectableElements.length > 0 && this.allVisibleSelected, { emitEvent: false });
  }

  private updateDisabledItems(): void {
    this.listboxElements.forEach((element) => {
      element.disabled = this.isItemDisabled(element.item);
    });
    this.updateToggleAllCheckbox();
    this.cdr.markForCheck();
  }

  private forceArray(source: any): any[] {
    return Array.isArray(source) ? source.slice() : [source];
  }
}
